import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Landmark, Loader2, AlertTriangle, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const fmtMoney = (v, cur) =>
  `${cur || ""} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const errMsg = (e) => e?.response?.data?.detail || "Could not reach NMB Bank.";

const Notice = ({ testid, children }) => (
  <div data-testid={testid} className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-6 text-amber-200 text-sm flex gap-3">
    <AlertTriangle size={18} className="shrink-0 mt-0.5" /> <div>{children}</div>
  </div>
);

const AccountCard = ({ acc, active, onSelect }) => (
  <button data-testid={`finance-account-${acc.account_id}`} onClick={onSelect}
    className={`text-left rounded-lg border p-6 transition-colors ${active ? "border-brand-blue bg-brand-blue/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wide text-gray-400 truncate">{acc.label}</span>
      <Landmark size={18} className="text-brand-yellow" />
    </div>
    <p data-testid={`finance-balance-${acc.account_id}`} className="text-2xl font-heading text-white">{fmtMoney(acc.balance, acc.currency)}</p>
    <p className="text-xs text-gray-500 mt-2 font-mono truncate">{acc.number}{acc.type ? ` · ${acc.type}` : ""}</p>
  </button>
);

const TxnTable = ({ txns }) => (
  <div className="rounded-lg bg-white/5 border border-white/10 overflow-x-auto" data-testid="finance-transactions-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-white/10 bg-white/5">
          {["Date", "Description", "Counterparty", "Type", "Amount", "Balance"].map((h) => (
            <th key={h} className="text-left px-4 py-4 text-xs uppercase tracking-wide text-gray-400 font-medium whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {txns.length === 0 ? (
          <tr><td colSpan={6} className="text-center py-16 text-gray-400">No transactions in this period.</td></tr>
        ) : txns.map((t) => (
          <tr key={t.id} data-testid={`finance-txn-${t.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
            <td className="px-4 py-4 text-gray-300 whitespace-nowrap">{fmtDate(t.date)}</td>
            <td className="px-4 py-4 text-gray-300 max-w-[260px] truncate">{t.description || "—"}</td>
            <td className="px-4 py-4 text-gray-300 whitespace-nowrap">{t.counterparty || "—"}</td>
            <td className="px-4 py-4 text-gray-400 whitespace-nowrap text-xs">{t.type || "—"}</td>
            <td className={`px-4 py-4 whitespace-nowrap font-medium ${t.amount < 0 ? "text-red-400" : "text-emerald-400"}`}>
              <span className="inline-flex items-center gap-1">
                {t.amount < 0 ? <ArrowUpRight size={14} /> : <ArrowDownLeft size={14} />}{fmtMoney(t.amount, t.currency)}
              </span>
            </td>
            <td className="px-4 py-4 text-gray-300 whitespace-nowrap">{fmtMoney(t.balance, t.currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const dateInput = "bg-[#0A2540] border border-white/20 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-brand-blue [color-scheme:dark]";

export const FinanceTab = () => {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [txns, setTxns] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [error, setError] = useState("");

  const loadTxns = async (acc, f = from, t = to) => {
    setTxLoading(true); setError("");
    try {
      const res = await api.get("/admin/finance/transactions", {
        params: { bank_id: acc.bank_id, account_id: acc.account_id, view_id: acc.view_id, from_date: f || undefined, to_date: t || undefined },
      });
      setTxns(res.data);
    } catch (e) { setError(errMsg(e)); } finally { setTxLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/admin/finance/status");
        setStatus(s.data);
        if (!s.data.configured) return;
        const a = await api.get("/admin/finance/accounts");
        setAccounts(a.data);
        if (a.data[0]) { setSelected(a.data[0]); loadTxns(a.data[0]); }
      } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const pick = (acc) => { setSelected(acc); loadTxns(acc); };
  const clear = () => { setFrom(""); setTo(""); if (selected) loadTxns(selected, "", ""); };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-brand-blue" size={36} /></div>;
  if (status && !status.configured) {
    return <Notice testid="finance-not-connected">NMB Bank is not connected yet. Add the NMB app key ({status.missing.join(", ")}) to the server settings to see balances and transactions.</Notice>;
  }

  return (
    <div className="space-y-8" data-testid="admin-finance">
      {status?.environment === "sandbox" && (
        <p data-testid="finance-sandbox-badge" className="inline-flex text-xs px-2.5 py-1 rounded-sm border border-amber-500/30 bg-amber-500/10 text-amber-300">
          NMB test sandbox — sample data, not your real account
        </p>
      )}
      {error && <Notice testid="finance-error">{error}</Notice>}
      {accounts.length === 0 && !error && <p className="text-gray-400 text-sm" data-testid="finance-no-accounts">No accounts found for this NMB login.</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {accounts.map((a) => <AccountCard key={a.account_id} acc={a} active={selected?.account_id === a.account_id} onSelect={() => pick(a)} />)}
      </div>
      {selected && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-400 flex flex-col gap-1">From
              <input data-testid="finance-from-date" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={dateInput} />
            </label>
            <label className="text-xs text-gray-400 flex flex-col gap-1">To
              <input data-testid="finance-to-date" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={dateInput} />
            </label>
            <Button data-testid="finance-apply-filter" onClick={() => loadTxns(selected)} className="bg-brand-blue hover:bg-brand-blue/90 rounded-sm">Apply</Button>
            <Button data-testid="finance-clear-filter" onClick={clear} variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 rounded-sm">Clear</Button>
          </div>
          {txLoading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-blue" size={28} /></div> : <TxnTable txns={txns} />}
        </div>
      )}
    </div>
  );
};
